import { useMutation, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import {
  WorkerCounter,
  FetchWorkerCountersParams,
  fetch,
  get,
  activate,
  deactivate,
  adjustValue,
  AdjustValueParams,
  CreateOrGetWorkerCounterParams,
  createOrGet,
} from '../api/worker-counters'
import { ApiErrorData } from '../api'

/**
 * Query hook to fetch worker counters with filters
 */
export const useWorkerCountersQuery = (
  filters?: FetchWorkerCountersParams,
  options?: QueryOptions<any>,
): UseQueryResult<WorkerCounter[]> =>
  useResources<WorkerCounter[], FetchWorkerCountersParams>(
    'worker-counters',
    fetch,
    filters || {},
    options,
  )

/**
 * Query hook to get a specific worker counter by ID
 */
export const useWorkerCounterQuery = (
  id?: string,
  options?: QueryOptions<any>,
): UseQueryResult<WorkerCounter> => useResource('worker-counters', get, id, undefined, options)

export const workerCounterKeys = {
  all: ['worker-counters'] as const,
  mutations: {
    activate: ['worker-counters', 'activate'] as const,
    deactivate: ['worker-counters', 'deactivate'] as const,
    adjustValue: ['worker-counters', 'adjust-value'] as const,
    createOrGet: ['worker-counters', 'create-or-get'] as const,
  },
} as const

/**
 * Callback function to invalidate worker counters cache on mutation success
 */
const onMutationSuccess = () => {
  queryClient.invalidateQueries(['worker-counters'])
}

/**
 * Mutation hook to activate a worker counter
 */
export const useActivateMutation = (): UseMutationResult<WorkerCounter, string> =>
  useMutation(activate, {
    mutationKey: workerCounterKeys.mutations.activate,
    onSuccess: onMutationSuccess,
  })

/**
 * Mutation hook to deactivate a worker counter
 */
export const useDeactivateMutation = (): UseMutationResult<WorkerCounter, string> =>
  useMutation(deactivate, {
    mutationKey: workerCounterKeys.mutations.deactivate,
    onSuccess: onMutationSuccess,
  })

/**
 * Mutation hook to adjust the value of a worker counter
 */
export const useAdjustValueMutation = (): UseMutationResult<
  WorkerCounter,
  AdjustValueParams,
  ApiErrorData
> =>
  useMutation(adjustValue, {
    mutationKey: workerCounterKeys.mutations.adjustValue,
    onSuccess: onMutationSuccess,
  })

/**
 * Mutation hook to create or get a counter for an organization
 */
export const useCreateOrGetCounterMutation = (): UseMutationResult<
  WorkerCounter,
  CreateOrGetWorkerCounterParams
> =>
  useMutation(createOrGet, {
    mutationKey: workerCounterKeys.mutations.createOrGet,
    onSuccess: onMutationSuccess,
  })
